// frontend/src/components/import-export/RecipeImportExport.tsx
// Menu item + recipe import/export using the generic ImportExportDialog
//
// Terminology:
//   - Menu Item (article du menu): what's sold to customers (e.g., "Burger with Fries")
//   - Recipe (recette): the ingredient breakdown for that menu item (bread, meat, fries, etc.)

import React, { useState } from "react";
import { Droplet, Weight, Boxes, Flame, type LucideIcon } from "lucide-react";
import { ImportExportDialog, type ImportExportConfig, type ImportValidationError, type ImportResult } from "./ImportExportDialog";
import { useRecipes } from "../../hooks/useInventoryData";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "../../services/api";
import type { Recipe } from "../../types/models";

// =============================================================================
// Export/Import Data Types (v2 structured format)
// =============================================================================

/** A single ingredient in a recipe */
interface RecipeIngredientData {
  resourceName: string;
  quantity: number;
  unit: string;
  costPrice: number;
}

/** v2 structured format: menu item clearly separated from its recipe */
export interface MenuItemWithRecipe {
  /** ISO date string — set for existing system items so the dialog can show "Créée le …" */
  _createdAt?: string;
  menuItem: {
    name: string;
    category: string | null;
    description: string | null;
    sellPrice: number;
    veloceItemId: string | null;
  };
  recipe: {
    calculatedCost: number;
    ingredients: RecipeIngredientData[];
  };
}

// =============================================================================
// Conversion helpers
// =============================================================================

function recipeToExportItem(recipe: Recipe): MenuItemWithRecipe {
  return {
    _createdAt: recipe.createdAt,
    menuItem: {
      name: recipe.name,
      category: recipe.category,
      description: recipe.description,
      sellPrice: Number(recipe.sellPrice) || 0,
      veloceItemId: recipe.veloceItemId,
    },
    recipe: {
      calculatedCost: Number(recipe.calculatedCost) || 0,
      ingredients: (recipe.ingredients || []).map((ing) => ({
        resourceName: ing.resource?.name || "?",
        quantity: ing.quantity,
        unit: ing.unit,
        costPrice: Number(ing.resource?.costPrice) || 0,
      })),
    },
  };
}

/**
 * Normalize imported data: accepts both v2 (structured) and v1 (flat) formats
 */
function normalizeImportData(parsed: any): MenuItemWithRecipe[] {
  const items: any[] = Array.isArray(parsed) ? parsed : parsed.data || [];

  return items.map((item) => {
    // v2 structured format (has menuItem key)
    if (item.menuItem) {
      return item as MenuItemWithRecipe;
    }
    // v1 flat format (legacy) — convert to v2
    return {
      _createdAt: item._createdAt,
      menuItem: {
        name: item.name,
        category: item.category ?? null,
        description: item.description ?? null,
        sellPrice: item.sellPrice ?? 0,
        veloceItemId: item.veloceItemId ?? null,
      },
      recipe: {
        calculatedCost: item.calculatedCost ?? 0,
        ingredients: item.ingredients || [],
      },
    } as MenuItemWithRecipe;
  });
}

// =============================================================================
// Formatters
// =============================================================================

function formatPrice(v: any): string {
  const n = Number(v);
  if (isNaN(n) || n === 0) return "—";
  return `${n.toFixed(2)} $`;
}

function formatIngredients(v: any): string {
  if (!v) return "Aucun";
  // Handle nested recipe.ingredients
  const arr = Array.isArray(v) ? v : v?.ingredients;
  if (!Array.isArray(arr) || arr.length === 0) return "Aucun";
  return arr.map((i: any) => `${i.resourceName} (${i.quantity} ${i.unit})`).join(", ");
}

// =============================================================================
// ImportExportConfig for menu items
// =============================================================================

/**
 * Group ingredients by unit-type (liquid / weight / piece / seasoning) and render
 * each group on its own line with a representative icon and the names inline.
 * Mid-density view used as the default in duplicate compare cards.
 */
const UNIT_GROUPS: {
  key: string;
  icon: LucideIcon;
  label: string;
  match: RegExp;
  /** CSS class for the icon color so groups are visually distinct */
  colorClass: string;
}[] = [
  {
    key: "liquid",
    icon: Droplet,
    label: "Liquides",
    match: /^(oz|ml|l|cl|litre|gallon)$/i,
    colorClass: "text-[var(--color-steel)]",
  },
  {
    key: "weight",
    icon: Weight,
    label: "Solides",
    match: /^(g|kg|mg|lb|gramme|kilo)$/i,
    colorClass: "text-[var(--color-amber)]",
  },
  {
    key: "season",
    icon: Flame,
    label: "Assaisonnement",
    match: /(dash|pinch|pinc[eé]e|trait)/i,
    colorClass: "text-[var(--color-brick)]",
  },
];
const PIECE_GROUP = {
  key: "piece",
  icon: Boxes,
  label: "Pièces",
  colorClass: "text-[var(--color-olive)]",
};

function getUnitGroup(unit: string) {
  for (const g of UNIT_GROUPS) {
    if (g.match.test(unit || "")) return g;
  }
  return PIECE_GROUP;
}

function renderRecipeMedium(item: MenuItemWithRecipe) {
  const ingredients = item.recipe.ingredients || [];
  if (ingredients.length === 0) {
    return <span className="text-[var(--muted-foreground)] italic text-xs">Aucun ingrédient</span>;
  }

  // Group ingredients by unit category, preserving first-seen order
  const grouped = new Map<
    string,
    { icon: LucideIcon; label: string; colorClass: string; items: typeof ingredients }
  >();
  for (const ing of ingredients) {
    const g = getUnitGroup(ing.unit);
    if (!grouped.has(g.key)) grouped.set(g.key, { icon: g.icon, label: g.label, colorClass: g.colorClass, items: [] });
    grouped.get(g.key)!.items.push(ing);
  }

  return (
    <div className="space-y-1 text-xs">
      {[...grouped.values()].map((g) => {
        const Icon = g.icon;
        return (
          <div key={g.label} className="flex items-start gap-2">
            <Icon
              className={`w-4 h-4 mt-0.5 flex-shrink-0 ${g.colorClass}`}
              strokeWidth={2.25}
              aria-label={g.label}
            />
            <div className="flex flex-wrap gap-x-2 gap-y-0.5 min-w-0">
              {g.items.map((ing, i) => (
                <span key={i} className="whitespace-nowrap">
                  <span className="text-[var(--foreground)]">{ing.resourceName}</span>
                  <span className="text-[var(--muted-foreground)]"> ({ing.quantity}{ing.unit ? ` ${ing.unit}` : ""})</span>
                  {i < g.items.length - 1 && <span className="text-[var(--muted-foreground)] opacity-50">,</span>}
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Render a compact one-line summary of a recipe (count + ingredient names) */
function renderRecipeSummary(item: MenuItemWithRecipe) {
  const ingredients = item.recipe.ingredients || [];
  if (ingredients.length === 0) {
    return <span className="text-[var(--muted-foreground)] italic">Aucun ingrédient</span>;
  }
  const names = ingredients.map((i) => i.resourceName).join(", ");
  return (
    <span className="text-[var(--muted-foreground)]">
      <span className="font-medium text-[var(--foreground)]">
        {ingredients.length} ingrédient{ingredients.length > 1 ? "s" : ""}
      </span>
      <span className="opacity-75"> · {names}</span>
    </span>
  );
}

/** Render ingredient detail panel for an import row */
function renderRecipeDetail(item: MenuItemWithRecipe) {
  const ingredients = item.recipe.ingredients || [];
  if (ingredients.length === 0) {
    return (
      <span className="text-[var(--muted-foreground)] italic">Aucun ingrédient</span>
    );
  }
  return (
    <div className="space-y-1">
      <div className="font-medium text-[var(--foreground)] mb-1.5">
        {ingredients.length} ingrédient{ingredients.length > 1 ? "s" : ""}
        {item.recipe.calculatedCost > 0 && (
          <span className="ml-2 font-normal text-[var(--muted-foreground)]">
            — Coût: {item.recipe.calculatedCost.toFixed(2)} $
          </span>
        )}
      </div>
      <div className="grid grid-cols-[1fr,auto,auto] gap-x-4 gap-y-0.5">
        {ingredients.map((ing, i) => (
          <React.Fragment key={i}>
            <span className="text-[var(--foreground)]">{ing.resourceName}</span>
            <span className="text-[var(--muted-foreground)] text-right tabular-nums">
              {ing.quantity} {ing.unit}
            </span>
            <span className="text-[var(--muted-foreground)] text-right tabular-nums">
              {ing.costPrice > 0 ? `${ing.costPrice.toFixed(2)} $` : "—"}
            </span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/** Per-ingredient diff for conflict comparison view */
function renderIngredientDiff(incoming: any, existing: any): React.ReactNode {
  const incomingArr: RecipeIngredientData[] = Array.isArray(incoming) ? incoming : incoming?.ingredients || [];
  const existingArr: RecipeIngredientData[] = Array.isArray(existing) ? existing : existing?.ingredients || [];

  const incomingMap = new Map(incomingArr.map(i => [i.resourceName, i]));
  const existingMap = new Map(existingArr.map(i => [i.resourceName, i]));
  const allNames = [...new Set([...incomingMap.keys(), ...existingMap.keys()])].sort();

  if (allNames.length === 0) return <span className="text-[var(--muted-foreground)] italic text-xs">Aucun ingrédient</span>;

  return (
    <div className="space-y-0.5 text-xs">
      {allNames.map(name => {
        const inc = incomingMap.get(name);
        const ext = existingMap.get(name);

        if (inc && !ext) {
          // Added
          return (
            <div key={name} className="flex items-center gap-2 px-2 py-1 rounded bg-[var(--color-olive-bg)]/50 text-[var(--color-olive)]">
              <span className="font-medium">+ {name}</span>
              <span className="ml-auto tabular-nums">{inc.quantity} {inc.unit}</span>
            </div>
          );
        }
        if (ext && !inc) {
          // Removed
          return (
            <div key={name} className="flex items-center gap-2 px-2 py-1 rounded bg-[var(--color-brick-bg)]/50 text-[var(--color-brick)] line-through">
              <span className="font-medium">- {name}</span>
              <span className="ml-auto tabular-nums">{ext.quantity} {ext.unit}</span>
            </div>
          );
        }
        if (inc && ext) {
          const qtyChanged = inc.quantity !== ext.quantity;
          const unitChanged = inc.unit !== ext.unit;
          if (qtyChanged || unitChanged) {
            // Modified
            return (
              <div key={name} className="flex items-center gap-2 px-2 py-1 rounded bg-[var(--color-amber-bg)]/50 text-[var(--color-amber)]">
                <span className="font-medium">{name}</span>
                <span className="ml-auto tabular-nums">
                  {ext.quantity} {ext.unit} → {inc.quantity} {inc.unit}
                </span>
              </div>
            );
          }
          // Identical
          return (
            <div key={name} className="flex items-center gap-2 px-2 py-1 text-[var(--foreground)]">
              <span>{name}</span>
              <span className="ml-auto tabular-nums text-[var(--muted-foreground)]">{inc.quantity} {inc.unit}</span>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

const menuItemConfig: ImportExportConfig<MenuItemWithRecipe> = {
  entityName: "article du menu",
  entityNamePlural: "articles du menu",
  // Identity key: prefer veloceItemId when present so two Veloce items sharing
  // a display name (e.g. multiple "BOISSON ENERGISANTE" rows from the POS) are
  // not treated as duplicates. Falls back to the lowercased name for items
  // without a Veloce link (e.g. manually created recipes).
  getKey: (item) =>
    item.menuItem.veloceItemId
      ? `vid:${item.menuItem.veloceItemId}`
      : `name:${item.menuItem.name.toLowerCase()}`,
  getDisplayName: (item) => item.menuItem.name,
  getCategory: (item) => item.menuItem.category || "",
  comparisonFields: [
    { label: "Nom", key: "menuItem.name", format: (v) => v || "—" },
    { label: "Catégorie", key: "menuItem.category", format: (v) => v || "—" },
    { label: "Recette (ingrédients)", key: "recipe.ingredients", format: formatIngredients, renderDiff: renderIngredientDiff },
  ],
  // Only compare recipe ingredients — menu item data comes from Veloce POS and shouldn't
  // affect conflict detection.
  isIdentical: (a, b) => {
    const aIngs = (a.recipe.ingredients || []).map((i) => `${i.resourceName}:${i.quantity}:${i.unit}`).sort();
    const bIngs = (b.recipe.ingredients || []).map((i) => `${i.resourceName}:${i.quantity}:${i.unit}`).sort();
    if (aIngs.length !== bIngs.length) return false;
    return aIngs.every((v, idx) => v === bIngs[idx]);
  },
  // When existing item has no recipe (empty ingredients) and import provides one,
  // treat as a simple update rather than a conflict — no resolution needed.
  isUpdate: (incoming, existing) => {
    const existingIngs = existing.recipe.ingredients || [];
    const incomingIngs = incoming.recipe.ingredients || [];
    return existingIngs.length === 0 && incomingIngs.length > 0;
  },
  renderDetail: renderRecipeDetail,
  renderSummary: renderRecipeSummary,
  renderMedium: renderRecipeMedium,
  getItemDate: (item) => item._createdAt,
};

// =============================================================================
// Validation
// =============================================================================

function validateMenuImport(parsed: any): ImportValidationError[] {
  const errors: ImportValidationError[] = [];

  // Must be an object
  if (typeof parsed !== "object" || parsed === null) {
    errors.push({ severity: "error", message: "Le fichier ne contient pas un objet JSON valide." });
    return errors;
  }

  // Check if it's an array directly (legacy format) or has a data array
  const items = Array.isArray(parsed) ? parsed : parsed.data;

  if (!items || !Array.isArray(items)) {
    errors.push({
      severity: "error",
      message: "Structure incompatible : le fichier doit contenir un tableau « data » avec les articles du menu.",
    });
    return errors;
  }

  if (items.length === 0) {
    errors.push({ severity: "error", message: "Le fichier ne contient aucun article." });
    return errors;
  }

  // Check version compatibility
  if (parsed.version && !["1.0", "2.0"].includes(parsed.version)) {
    errors.push({
      severity: "warning",
      message: `Version « ${parsed.version} » non reconnue. Les versions supportées sont 1.0 et 2.0. L'import pourrait échouer.`,
    });
  }

  // Sample first few items to validate structure
  const sample = items.slice(0, 5);
  const isV2 = sample.some((item: any) => item.menuItem);
  const isV1 = sample.some((item: any) => item.name && !item.menuItem);
  const isNeither = sample.some((item: any) => !item.menuItem && !item.name);

  if (isNeither && !isV1 && !isV2) {
    errors.push({
      severity: "error",
      message: "Les éléments du fichier ne correspondent pas au format attendu. Chaque élément doit avoir « menuItem.name » (v2) ou « name » (v1).",
    });
    return errors;
  }

  // Validate item structure
  let missingNames = 0;
  let missingIngredients = 0;

  for (const item of items) {
    const name = item.menuItem?.name ?? item.name;
    if (!name) missingNames++;

    const ingredients = item.recipe?.ingredients ?? item.ingredients;
    if (!ingredients || !Array.isArray(ingredients) || ingredients.length === 0) {
      missingIngredients++;
    }
  }

  if (missingNames > 0) {
    errors.push({
      severity: "error",
      message: `${missingNames} élément(s) sans nom détecté(s). Chaque article doit avoir un nom.`,
    });
  }

  if (missingIngredients > 0 && missingIngredients < items.length) {
    errors.push({
      severity: "warning",
      message: `${missingIngredients} article(s) sur ${items.length} n'ont pas de recette (ingrédients). Ils seront importés sans recette.`,
    });
  }

  if (missingIngredients === items.length) {
    errors.push({
      severity: "warning",
      message: "Aucun article ne contient de recette (ingrédients). Seuls les articles du menu seront importés.",
    });
  }

  return errors;
}

// =============================================================================
// Component
// =============================================================================

interface RecipeImportExportProps {
  isOpen: boolean;
  onClose: () => void;
}

export function RecipeImportExport({ isOpen, onClose }: RecipeImportExportProps) {
  const { data: recipes = [] } = useRecipes();
  const queryClient = useQueryClient();
  const [importing, setImporting] = useState(false);

  // Convert current recipes to structured export format for comparison
  const existingItems: MenuItemWithRecipe[] = recipes.map(recipeToExportItem);

  const handleExport = (items: MenuItemWithRecipe[]) => {
    const exportData = {
      exportedAt: new Date().toISOString(),
      version: "2.0",
      description: "Menu items with recipes (ingredient breakdowns)",
      count: items.length,
      data: items,
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `menu_${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = async (
    items: { data: MenuItemWithRecipe; resolution?: "incoming" | "existing" }[],
    onProgress?: (current: number, total: number) => void,
  ): Promise<ImportResult> => {
    const toImport = items
      .filter((item) => item.resolution !== "existing")
      .map((item) => item.data);

    if (toImport.length === 0) return { succeeded: 0, failed: [], autoCreatedResources: [] };

    setImporting(true);
    const result: ImportResult = { succeeded: 0, failed: [], autoCreatedResources: [] };
    // Dedupe across batches — a resource auto-created in batch #1 should not be
    // re-listed if another recipe in batch #2 references it (it now exists in DB).
    const autoCreatedSet = new Set<string>();
    const batchSize = 10;
    const total = toImport.length;

    try {
      // Batch items for meaningful progress
      for (let i = 0; i < total; i += batchSize) {
        const batch = toImport.slice(i, i + batchSize);
        try {
          const res = await api.post("/data/import/menu", {
            data: batch,
            mode: "upsert",
          });
          result.succeeded += batch.length;
          // Pull auto-created resource names from the backend response and dedupe
          const created: string[] = res.data?.data?.details?.autoCreatedResources ?? [];
          for (const name of created) autoCreatedSet.add(name);
        } catch (err: any) {
          // Mark all items in failed batch
          batch.forEach(item => {
            result.failed.push({
              name: item.menuItem.name,
              error: err?.response?.data?.message || err?.message || "Erreur inconnue",
            });
          });
        }
        onProgress?.(Math.min(i + batchSize, total), total);
      }
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
    } finally {
      setImporting(false);
    }

    result.autoCreatedResources = [...autoCreatedSet].sort();
    return result;
  };

  const handleReset = async (): Promise<boolean> => {
    try {
      await api.post("/data/reset", { confirm: true });
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      return true;
    } catch (err) {
      console.error("Reset failed:", err);
      toast.error("Erreur lors de la réinitialisation.");
      return false;
    }
  };

  return (
    <ImportExportDialog<MenuItemWithRecipe>
      isOpen={isOpen}
      onClose={onClose}
      config={menuItemConfig}
      existingItems={existingItems}
      onExport={handleExport}
      onImport={handleImport}
      parseImportData={normalizeImportData}
      validateImportData={validateMenuImport}
      onReset={handleReset}
    />
  );
}
